import * as React from 'react';
import { compose, withProps, branch, renderNothing } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { CellIndex, DeviceId, CameraCommand } from 'types';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { State } from 'reducers';
import { getCamera } from 'selectors/deviceSelectors';
import { cameraFn } from 'actions/cameraActions';
import { Button } from './Button';
import { PtzControls } from './PtzControls';
import { isAdmin } from 'selectors/shellSelectors';

import './ImageControls.css';
import { getLayout } from 'selectors/monitoringSelectors';

const images = {
    brightnessUp: require('assets/images/brightness+.png'),
    brightnessDown: require('assets/images/brightness-.png'),
    contrastUp: require('assets/images/contrast+.png'),
    contrastDown: require('assets/images/contrast-.png'),
    focusUp: require('assets/images/focus+.png'),
    focusDown: require('assets/images/focus-.png'),
    irisUp: require('assets/images/iris+.png'),
    irisDown: require('assets/images/iris-.png'),
};

type Props = OwnProps & StateProps & DispProps & RecomposeProps & TranslateProps;
export const RawImageControls = ({ cellIndex, deviceId, canControlPtz, showImageControls, t, styles, ...props }: Props) =>
    <div
        className={ `image-controls-host ${canControlPtz ? 'with-ptz-controls' : ''}` }
        style={ styles.host }
    >
        { canControlPtz &&
            <PtzControls
                cellIndex={ cellIndex }
                deviceId={ deviceId }
            />
        }
        { showImageControls &&
            <div className="image-controls">
                <div className="button-row">
                    <Button
                        id={ `iris-up-${cellIndex}` }
                        titleKey="6.1237.3324"
                        imgSrc={ images.irisUp }
                        onClick={ props.onClickIrisUp }
                    />
                    <Button
                        id={ `iris-down-${cellIndex}` }
                        titleKey="6.1237.3325"
                        imgSrc={ images.irisDown }
                        onClick={ props.onClickIrisDown }
                    />
                </div>
                <div className="button-row">
                    <Button
                        id={ `brightness-up-${cellIndex}` }
                        titleKey="6.1237.3328"
                        imgSrc={ images.brightnessUp }
                        onClick={ props.onClickBrightnessUp }
                    />
                    <Button
                        id={ `brightness-down-${cellIndex}` }
                        titleKey="6.1237.3329"
                        imgSrc={ images.brightnessDown }
                        onClick={ props.onClickBrightnessDown }
                    />
                </div>
                <div className="button-row">
                    <Button
                        id={ `focus-up-${cellIndex}` }
                        titleKey="6.1237.3326"
                        imgSrc={ images.focusUp }
                        onClick={ props.onClickFocusUp }
                    />
                    <Button
                        id={ `focus-down-${cellIndex}` }
                        titleKey="6.1237.3327"
                        imgSrc={ images.focusDown }
                        onClick={ props.onClickFocusDown }
                    />
                </div>
                <div className="button-row">
                    <Button
                        id={ `contrast-up-${cellIndex}` }
                        titleKey="6.1237.3330"
                        imgSrc={ images.contrastUp }
                        onClick={ props.onClickContrastUp }
                    />
                    <Button
                        id={ `contrast-down-${cellIndex}` }
                        titleKey="6.1237.3331"
                        imgSrc={ images.contrastDown }
                        onClick={ props.onClickContrastDown }
                    />
                </div>
            </div>
        }
    </div>
;

type OwnProps = {
    cellIndex: CellIndex;
    deviceId: DeviceId;
};
type StateProps = {
    canControlPtz: boolean;
    showImageControls: boolean;
};
type DispProps = {
    onClickIrisUp: () => void;
    onClickIrisDown: () => void;
    onClickBrightnessUp: () => void;
    onClickBrightnessDown: () => void;
    onClickFocusUp: () => void;
    onClickFocusDown: () => void;
    onClickContrastUp: () => void;
    onClickContrastDown: () => void;
};
type RecomposeProps = {
    styles: {
        host: React.CSSProperties;
    };
};

const mapStateToProps = (state: State, ownProps: OwnProps): StateProps => {
    const camera = getCamera(state, ownProps);
    return {
        canControlPtz: camera ? camera.canControlPtz : false,
        showImageControls: isAdmin(state) && getLayout(state) === 1,
    };
};
const mapDispatchToProps = (dispatch: Function, { deviceId }: OwnProps): DispProps => {
    const makeDisp = (commandId: CameraCommand) => () => dispatch(cameraFn({ deviceId, commandId }));
    return {
        onClickIrisUp:         makeDisp(CameraCommand.IRIS1),
        onClickIrisDown:       makeDisp(CameraCommand.IRIS2),
        onClickBrightnessUp:   makeDisp(CameraCommand.BRIGHT1),
        onClickBrightnessDown: makeDisp(CameraCommand.BRIGHT2),
        onClickFocusUp:        makeDisp(CameraCommand.FOCUS1),
        onClickFocusDown:      makeDisp(CameraCommand.FOCUS2),
        onClickContrastUp:     makeDisp(CameraCommand.CONTRAST1),
        onClickContrastDown:   makeDisp(CameraCommand.CONTRAST2),
    };
};

export const ImageControls = compose<Props, OwnProps>(
    skin,
    translate(),
    connect<StateProps, DispProps, {}>(mapStateToProps, mapDispatchToProps),
    withProps<OemProps, OemProps>(({ styles }) => ({
        styles: {
            host: {
                backgroundColor: styles.Input.backgroundColor,
            },
        },
    })),
    branch<StateProps>(({ canControlPtz, showImageControls }) => !canControlPtz && !showImageControls, renderNothing),
)(RawImageControls);



// WEBPACK FOOTER //
// ./src/ui/pages/monitoring/mainPanel/controls/ImageControls.tsx